import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';

interface WalletBalanceCardProps {
  balance: number;
  isLoading?: boolean;
}

/**
 * Format amount in Indian currency format (lakhs/crores)
 */
const formatIndianCurrency = (amount: number): string => {
  const formatter = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return formatter.format(amount);
};

export const WalletBalanceCard: React.FC<WalletBalanceCardProps> = ({
  balance,
  isLoading = false,
}) => {
  const navigate = useNavigate();
  // Ensure balance is a valid number, default to 0 if undefined/null/NaN
  const safeBalance = typeof balance === 'number' && !isNaN(balance) ? balance : 0;
  const isLowBalance = safeBalance < 50;

  return (
    <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-2xl p-6 text-white shadow-lg">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-primary-100 text-sm font-medium">Wallet Balance</p>
          {isLoading ? (
            <div className="mt-2 h-10 w-32 bg-primary-500/50 rounded animate-pulse"></div>
          ) : (
            <p className="mt-2 text-4xl font-bold tracking-tight">
              {formatIndianCurrency(safeBalance)}
            </p>
          )}
        </div>
        <div className="p-3 bg-white/10 rounded-xl">
          <svg
            className="w-8 h-8"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
            />
          </svg>
        </div>
      </div>

      {/* Low Balance Warning */}
      {!isLoading && isLowBalance && (
        <div className="mt-4 flex items-center gap-2 bg-amber-500/20 text-amber-100 px-3 py-2 rounded-lg">
          <svg
            className="w-5 h-5 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <span className="text-sm font-medium">
            Low balance! Add credits to continue purchasing Q-Leads.
          </span>
        </div>
      )}

      <div className="mt-6">
        <Button
          onClick={() => navigate('/wallet/topup')}
          variant="secondary"
          className="bg-white text-primary-700 hover:bg-primary-50"
          leftIcon={
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
          }
        >
          Add Credits
        </Button>
      </div>
    </div>
  );
};

export default WalletBalanceCard;
